import { ref } from "vue"
import type { RouteRecordRaw } from "vue-router"
import { defineStore } from "pinia"
import * as userApis from "@/api/userApis"
import { removeRoutes } from "@/router"
import store from "./index"
import router from "@/router"
import { IUserInfo } from "@/store/root-types"
import { ResponseCodeEnum, StorageKeysEnum } from "@/constants"
import { isAnyMatch } from "@/utils"

const useUserStore = defineStore("user", () => {
  const userInfo = ref<IUserInfo | null>(null)
  
  const routes = ref<RouteRecordRaw[]>([])
  
  const returnCompany = ref(false)
  
  const collapse = ref(false)
  
  // 1 抖音 2 快手 3 全部
  // 用于切换
  const globalIndex = ref<1 | 2>((Number(localStorage.getItem("globalIndex")) as 1 | 2) || 1)
  // 组件内只读
  const auth_type = ref<1 | 2 | 3>(1)
  
  /* const performanceStatistics = async (tag: string, callback: () => Promise<void> | void) => {
      const start = new Date().getTime();
      await callback();
      const end = new Date().getTime();
      const time = end - start;
      console.log(tag, `耗时: ${time}, 开始: ${start}, 结束: ${end}`);
  }; */
  
  const login = async (data: userApis.LoginBody) => {
    try {
      const response = await userApis.login(data)
      if (response.data.code === ResponseCodeEnum.SUCCESS) {
        const _userInfo = response.data.data
        userInfo.value = _userInfo
        
        localStorage.setItem(StorageKeysEnum.TOKEN, _userInfo.token)
        localStorage.setItem(StorageKeysEnum.USER_INFO, JSON.stringify(_userInfo))
        return userInfo.value
      }
    }
    catch (error) {
      console.error(error)
    }
    return null
  }
  
  const logout = async () => {
    try {
      const response = await userApis.logout()
      // 退出登录成功或者 Token 失效了
      if (isAnyMatch(response.data.code, ResponseCodeEnum.SUCCESS, ResponseCodeEnum.TOKEN_INVALID)) {
        resetToken()
        
        return true
      }
      return false
    }
    catch (error) {
      console.error(error)
    }
  }
  
  const getUserInfo = async () => {
    try {
      const response = await userApis.getUserInfo()
      if (response.data.code === ResponseCodeEnum.SUCCESS) {
        const _userInfo = response.data.data
        userInfo.value = _userInfo
        
        if (userInfo.value.company) {
          if (userInfo.value.company.auth_type === 3) {
            auth_type.value = userInfo.value.company.auth_type
          }
          else {
            globalIndex.value = userInfo.value.company.auth_type as 1 | 2
            auth_type.value = userInfo.value.company.auth_type
          }
        }
        
        const favicon: any = document.querySelector("link[rel=\"icon\"]")
        if (favicon && userInfo.value.company?.icon) {
          favicon.href = userInfo.value.company.icon
        }
        // console.log('userInfo.company',userInfo.value.company?.icon,favicon);
        // console.log('favicon', document.querySelector('link[rel="icon"]'));
        localStorage.setItem(StorageKeysEnum.USER_INFO, JSON.stringify(_userInfo))
        return _userInfo
      }
    }
    catch (error) {
      console.error(error)
    }
    return null
  }
  
  const updatePassword = async (data: userApis.UpdatePasswordBody) => {
    try {
      const response = await userApis.updatePassword(data)
      if (response.data.code === ResponseCodeEnum.SUCCESS) {
        localStorage.removeItem(StorageKeysEnum.TOKEN)
        localStorage.removeItem(StorageKeysEnum.USER_INFO)
        router.push({ path: "/login" })
        return true
      }
    }
    catch (error) {
      console.error(error)
    }
    return false
  }
  
  const resetToken = () => {
    userInfo.value = null
    
    removeRoutes(routes.value)
    routes.value = []
    
    localStorage.removeItem(StorageKeysEnum.TOKEN)
    localStorage.removeItem(StorageKeysEnum.USER_INFO)
    
    localStorage.removeItem("globalIndex")
    localStorage.removeItem("partnerInfo")
  }
  
  const addRoutes = (_routes: RouteRecordRaw[]) => {
    routes.value = [...routes.value, ..._routes]
  }
  
  const replaceRoutes = (_routes: RouteRecordRaw[]) => {
    routes.value = [..._routes]
  }
  
  return {
    userInfo,
    routes,
    collapse,
    returnCompany,
    globalIndex,
    auth_type,
    login,
    logout,
    getUserInfo,
    updatePassword,
    resetToken,
    addRoutes,
    replaceRoutes
  }
})

const hasPermission = (roles: string[], route: RouteRecordRaw) => {
  const rolesFromRoute = route.meta?.roles
  
  if (rolesFromRoute == null || !(rolesFromRoute instanceof Array)) {
    return true
  }
  
  const rolesMap = roles.reduce<Record<string, boolean>>((previousValue, currentValue) => {
    previousValue[currentValue] = true
    return previousValue
  }, {})
  
  return rolesFromRoute.some((role) => rolesMap[role])
}

const filterAsyncRoutes = (roles: string[], routes: RouteRecordRaw[]) => {
  return routes.filter((route) => hasPermission(roles, route))
}

export const userStore = () => useUserStore(store)

export default useUserStore
